interface CertificateDocument {
  id: number;
  name: string;
  file: string;
  slug: string;
  [key: string]: any;
}

interface RawSubcategory {
  id: number;
  name: string;
  skus?: string[];
  [key: string]: any;
}

interface RawCategory {
  id: number;
  name: string;
  children?: RawSubcategory[];
  [key: string]: any;
}

/**
 * A subcategory as the certificates page consumes it: `skus` no longer holds
 * SKU strings but the certificate documents published for those SKUs.
 */
export interface CertificateSubcategory extends Omit<RawSubcategory, 'skus'> {
  skus: CertificateDocument[];
}

export interface CertificateCategory extends Omit<RawCategory, 'children'> {
  children: CertificateSubcategory[];
}

/**
 * Loader for the certificates index: categories that have children, with every
 * child's SKUs swapped for its certificate documents and childless entries
 * dropped.
 *
 * Returns the payload — the page owns it through `useAsyncData`.
 */
export default function useCertificateCategories() {
  const { locale } = useI18n();
  const {
    $api: { categoryService, documentService },
  } = useNuxtApp();

  const fetch = async (): Promise<CertificateCategory[]> => {
    const [{ data: categories }, { data: documentsBySku }] = (await Promise.all(
      [
        categoryService.getCategoriesWithProductSkus({
          lang: locale.value,
        } as any),
        documentService.getDocumentsGroupedBySku({
          type: 'Certificates',
          lang: locale.value,
        } as any),
      ],
    )) as unknown as [
      { data: RawCategory[] },
      { data: Record<string, CertificateDocument[] | undefined> },
    ];

    const withDocuments = (
      subcategory: RawSubcategory,
    ): CertificateSubcategory => ({
      ...subcategory,
      skus: (subcategory.skus ?? [])
        .flatMap((sku) => documentsBySku[sku] ?? [])
        // The grouped response has been seen with holes in it
        .filter((document) => document !== undefined),
    });

    return categories
      .filter((category) => category.children?.length)
      .map((category) => ({
        ...category,
        children: (category.children ?? [])
          .map(withDocuments)
          .filter((child) => child.skus.length),
      }));
  };

  return { fetch };
}
